import { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { showSuccessToast, showErrorToast } from '../../utils/notificationUtils';
import { outpatientAPI, inpatientAPI } from '../../services/api';
import '../../pages/reception/ReceptionStyles.css';

const SOAPDetailsContent = ({
  isVisible,
  onClose,
  outpatientId,
  inpatientId,
  onSave,
  finalDiagnosis,
  visitInfo,
  services = [],
  onInitialDiagnosisUpdate,
  visitInitialDiagnosis,
  readOnly = false,
  petId,
  hideHeader = false,
  // Controls for Plan section behavior
  showPlanServices = true,
  planServicesMode = 'all', // 'all' | 'fulfilledOnly'
  allowAutoLoadServices = true,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [soapData, setSoapData] = useState({
    serial: '',
    dateTime: new Date().toISOString().slice(0, 16),
    ownerComplaint: '',
    subjective: '',
    severity: '',
    objective: '',
    initialDiagnosis: '',
    assessment: '',
    plan: '',
    history: [], // Stores previous differential diagnoses
    finalDiagnosis: '' // Added for read-only view
  });

  const [vitalSigns, setVitalSigns] = useState({
    serial: '',
    temperature: '',
    heartRate: '',
    respiratoryRate: '',
    capillaryRefillTime: '',
    bodyWeight: '',
    hydrationStatus: '',
    mucousMembraneColor: '',
    bodyConditionScore: '',
    urine: '',
    stool: '',
    oralCavity: '',
    lymphNodes: ''
  });

  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [errors, setErrors] = useState({});
  const [openSections, setOpenSections] = useState({
    subjective: readOnly,
    objective: readOnly,
    assessment: readOnly,
    plan: readOnly
  });

  // Day Selector States
  const [selectedDay, setSelectedDay] = useState(1);
  const [days, setDays] = useState([]);
  const [allVitalSigns, setAllVitalSigns] = useState({}); // Map of Day -> Record

  // Determine visit ID and type
  const visitId = inpatientId || outpatientId;
  const visitType = inpatientId ? 'inpatient' : 'outpatient';

  // Use refs to track if we've already fetched to prevent duplicate calls
  const hasFetchedSOAPRef = useRef(false);
  const hasFetchedVitalSignsRef = useRef(false);
  const hasFetchedVisitInfoRef = useRef(false);
  const hasFetchedServicesRef = useRef(false);

  const [internalVisitInfo, setInternalVisitInfo] = useState(null);
  const [internalServices, setInternalServices] = useState([]);
  const effectiveVisitInfo = visitInfo || internalVisitInfo;
  const effectiveServices = (services && services.length > 0) ? services : internalServices;
  const planServices = useMemo(() => {
    if (!effectiveServices || effectiveServices.length === 0) return [];
    if (planServicesMode === 'fulfilledOnly') {
      return effectiveServices.filter(s => !!(s.is_fulfilled ?? s.fulfilled));
    }
    return effectiveServices;
  }, [effectiveServices, planServicesMode]);

  // Reset fetch flags when visitId changes to ensure fresh data (e.g., viewing past visits or different accounts)
  useEffect(() => {
    hasFetchedSOAPRef.current = false;
    hasFetchedVitalSignsRef.current = false;
    hasFetchedVisitInfoRef.current = false;
    hasFetchedServicesRef.current = false;
  }, [visitId]);

  // Load existing SOAP data and vital signs when component becomes visible
  useEffect(() => {
    if (isVisible && visitId) {
      if (!visitInfo && !hasFetchedVisitInfoRef.current) {
        loadVisitInfo();
        hasFetchedVisitInfoRef.current = true;
      }

      // Only fetch if we haven't fetched for this visit yet
      if (!hasFetchedSOAPRef.current) {
        loadExistingSOAPData();
        hasFetchedSOAPRef.current = true;
      }
      if (!hasFetchedVitalSignsRef.current) {
        loadExistingVitalSigns();
        hasFetchedVitalSignsRef.current = true;
      }

      // Fetch services if not provided (unless explicitly disabled)
      if (allowAutoLoadServices && (!services || services.length === 0) && !hasFetchedServicesRef.current) {
        loadServices();
        hasFetchedServicesRef.current = true;
      }
    } else if (!isVisible) {
      // Reset refs when component closes
      hasFetchedSOAPRef.current = false;
      hasFetchedVitalSignsRef.current = false;
      hasFetchedVisitInfoRef.current = false;
      hasFetchedServicesRef.current = false;
    }
  }, [isVisible, visitId, visitType, allowAutoLoadServices]);

  // Auto-populate Initial Diagnosis from visit's initial diagnosis if it exists and SOAP Initial Diagnosis is empty
  useEffect(() => {
    if (visitInitialDiagnosis && visitInitialDiagnosis.trim() && !soapData.initialDiagnosis.trim() && (!soapData.history || soapData.history.length === 0)) {
      setSoapData(prev => ({
        ...prev,
        initialDiagnosis: visitInitialDiagnosis
      }));
    }
  }, [visitInitialDiagnosis, soapData.initialDiagnosis]);

  const loadExistingSOAPData = async () => {
    if (!visitId) return;

    setLoadingExisting(true);
    try {
      const token = localStorage.getItem('access_token');
      const endpoint = visitType === 'inpatient'
        ? `${import.meta.env.VITE_API_URL}/api/v1/soap-notes/inpatient-visit/${visitId}`
        : `${import.meta.env.VITE_API_URL}/api/v1/soap-notes/outpatient-visit/${visitId}`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data && data.data.length > 0) {
          // Use the most recent SOAP record
          const latestSOAP = data.data[0];
          setSoapData({
            serial: latestSOAP.serial || '',
            dateTime: latestSOAP.date_time || new Date().toISOString().slice(0, 16),
            ownerComplaint: latestSOAP.owner_complaint || '',
            subjective: latestSOAP.subjective || '',
            severity: latestSOAP.severity || '',
            objective: latestSOAP.objective || '',
            // Use SOAP initial diagnosis if it exists, otherwise fall back to visit's initial diagnosis
            initialDiagnosis: latestSOAP.initial_diagnosis || visitInitialDiagnosis || '',
            assessment: latestSOAP.assessment || '',
            plan: latestSOAP.plan || '',
            history: latestSOAP.diagnosis_history || [],
            finalDiagnosis: latestSOAP.final_diagnosis || ''
          });

          // If plan is empty and we have services, we could auto-fill it, 
          // but better to just display them in the render logic to avoid polluting the DB field unnecessarily 
          // unless the user wants to edit it.
        } else {
          // No existing SOAP data, auto-populate initial diagnosis from visit if it exists
          if (visitInitialDiagnosis && visitInitialDiagnosis.trim()) {
            setSoapData(prev => ({
              ...prev,
              initialDiagnosis: visitInitialDiagnosis
            }));
          }
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message ||
          (errorData.errors ? Object.values(errorData.errors).flat().join(', ') : null) ||
          'Failed to load SOAP notes';
        showErrorToast(errorMessage, {
          title: 'Loading Error',
          duration: 5000
        });
      }
    } catch (error) {
      showErrorToast('An error occurred while loading SOAP notes. Please try again.', {
        title: 'Error',
        duration: 5000
      });
    } finally {
      setLoadingExisting(false);
    }
  };

  const loadVisitInfo = async () => {
    if (!visitId) return;
    try {
      const response = await (visitType === 'inpatient'
        ? inpatientAPI.getInpatientVisit(visitId)
        : outpatientAPI.getOutpatientVisit(visitId));

      const rawResponse = response.data;
      const data = rawResponse?.status === 'success' ? rawResponse.data : rawResponse;

      if (data) {
        // Handle cases where data is an array (services only) vs object (visit info + services)
        const visit = (!Array.isArray(data) && (data.visit || data.inpatient_visit || data.outpatient_visit || data))
          ? (data.visit || data.inpatient_visit || data.outpatient_visit || data)
          : null;

        if (visit && typeof visit === 'object') {
          setInternalVisitInfo({
            visitDate: visit.visit_date || visit.created_at || visit.admission_date,
            initialDiagnosis: visit.initial_diag || visit.initial_diagnosis || visit.reason || visit.complaint || '',
            finalDiagnosis: visit.final_diag || visit.final_diagnosis || '',
            doctorNotes: visit.doctor_notes || '',
            prescription: visit.prescription || ''
          });
        }
      }
    } catch (err) {}
  };

  const loadServices = async () => {
    if (!visitId) return;
    try {
      // Try the primary endpoint based on visitType
      let response = await (visitType === 'inpatient'
        ? inpatientAPI.getInpatientVisit(visitId)
        : outpatientAPI.getOutpatientVisit(visitId));

      let rawResponse = response.data;
      let data = rawResponse?.status === 'success' ? rawResponse.data : rawResponse;

      // Deep helper to find services array in unknown structures
      const findServices = (obj) => {
        if (!obj) return [];
        if (Array.isArray(obj)) return obj;
        return obj.services || obj.visit_details || obj.visitDetails ||
          obj.inpatient_visit_details || obj.outpatient_visit_details ||
          obj.details || obj.items || [];
      };

      let rawServices = findServices(data);

      // Also check if the services were already provided in the visitInfo prop for fallbacks
      if (rawServices.length === 0 && visitInfo?.services) {
        rawServices = findServices(visitInfo.services);
      }

      // If visit key exists, check inside it too
      if (rawServices.length === 0 && data && typeof data === 'object') {
        const visitObj = data.visit || data.inpatient_visit || data.outpatient_visit;
        if (visitObj) rawServices = findServices(visitObj);
      }

      // If still empty, try the other visit type as a fallback (sometimes IDs or types are crossed in EMR)
      if (rawServices.length === 0) {
        try {
          const fallbackResponse = await (visitType === 'inpatient'
            ? outpatientAPI.getOutpatientVisit(visitId)
            : inpatientAPI.getInpatientVisit(visitId));
          const fallbackData = fallbackResponse.data?.status === 'success' ? fallbackResponse.data.data : fallbackResponse.data;
          rawServices = findServices(fallbackData);
          if (rawServices.length === 0 && fallbackData?.visit) rawServices = findServices(fallbackData.visit);
        } catch (_) {
          // Ignore fallback error
        }
      }

      if (rawServices && Array.isArray(rawServices)) {
        const normalized = rawServices.map(s => {
          // Extremely robust name extraction
          const name = s.clinic_service?.service_name_en ||
            s.service?.service_name_en ||
            s.clinic_service?.name ||
            s.service?.name ||
            s.service_name_en ||
            s.service_name ||
            s.name ||
            s.title ||
            `Service #${s.id || '?'}`;

          return {
            ...s,
            id: s.id,
            name: name,
            qty: s.qty || s.quantity || 1,
            fulfilled: s.is_fulfilled !== undefined ? !!s.is_fulfilled : (s.fulfilled !== undefined ? !!s.fulfilled : true),
            notes: s.notes || ''
          };
        });
        setInternalServices(normalized);
      }
    } catch (err) {}
  };

  const handleAddDifferential = () => {
    if (!soapData.initialDiagnosis.trim()) return;

    // Get current user info
    let doctorName = 'Doctor';
    try {
      const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
      if (userData.name) {
        doctorName = `Dr. ${userData.name}`;
      } else if (userData.first_name) {
        doctorName = `Dr. ${userData.first_name} ${userData.last_name || ''}`;
      }
    } catch (e) {}

    const newEntry = {
      diagnosis: soapData.initialDiagnosis,
      date: new Date().toISOString(),
      by: doctorName
    };

    setSoapData(prev => ({
      ...prev,
      history: [...(prev.history || []), newEntry], // Append new entry to history
      initialDiagnosis: '' // Clear current field for new input
    }));
  };

  const loadPreviousSOAPData = async () => {
    if (!petId || soapData.history || soapData.ownerComplaint) return; // Don't overwrite if user already typed or if we have data

    try {
      const token = localStorage.getItem('access_token');
      // Search for previous inpatient visits for this pet
      // We'll use the search-inpatient-visits endpoint to find the last visit

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/clinic-appointments/search-inpatient-visits?pet_id=${petId}&sort_by=date&sort_order=desc`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const json = await response.json();
        const visits = json.data?.inpatient_visits || [];

        // Find the most recent visit that IS NOT the current one
        const previousVisit = visits.find(v => String(v.id) !== String(inpatientId));

        if (previousVisit) {
          // Now fetch the SOAP for this previous visit to get history/complaint if available
          // Or if the visit itself has some history fields, we use them.
          // Typically history/complaint is in SOAP.
          const soapResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/soap-notes/inpatient-visit/${previousVisit.id}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (soapResponse.ok) {
            const soapJson = await soapResponse.json();
            if (soapJson.data && soapJson.data.length > 0) {
              const lastSoap = soapJson.data[0];

              // Pre-fill history and complaint
              setSoapData(prev => ({
                ...prev,
                ownerComplaint: lastSoap.owner_complaint || prev.ownerComplaint,
                // We could also pre-fill objective or others if requested, but usually history/complaint is what users want carried over for re-admission
              }));

              showSuccessToast('History pre-filled from previous visit', { duration: 3000 });
            }
          }
        }
      }
    } catch (err) {}
  };

  // Trigger pre-fill after loading existing data if no data was found
  useEffect(() => {
    if (!loadingExisting && isVisible && !soapData.serial && petId) {
      // If we finished loading existing and found nothing (no serial), try to pre-fill
      loadPreviousSOAPData();
    }
  }, [loadingExisting, isVisible, soapData.serial, petId]);

  const loadExistingVitalSigns = async () => {
    if (!visitId) return;

    try {
      const endpoint = visitType === 'inpatient'
        ? `${import.meta.env.VITE_API_URL}/api/v1/vital-signs/inpatient-visit/${visitId}`
        : `${import.meta.env.VITE_API_URL}/api/v1/vital-signs/outpatient-visit/${visitId}`;

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();

        // Always calculate days if visitDate is available
        const visitDate = effectiveVisitInfo?.visitDate;
        if (visitDate) {
          const startDate = new Date(visitDate);
          startDate.setHours(0, 0, 0, 0);

          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const diffTime = today - startDate;
          const diffDays = Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));
          const totalDays = diffDays + 1;
          const daysArray = Array.from({ length: totalDays }, (_, i) => i + 1);
          setDays(daysArray);

          // Set initial selected day if not already set (default to last day)
          // But we don't want to override user selection if they are browsing
          // Only set if we haven't set it yet? Or if it's the first load?
          // Actually, we usually want to default to today/last day on load.
          // Let's check if selectedDay is valid, if not (or if it's 1 and we have more days), maybe update?
          // Safer to just ensure selectedDay is within range after calculation.
          if (daysArray.length > 0 && (!selectedDay || selectedDay > totalDays)) {
            setSelectedDay(daysArray[daysArray.length - 1]);
          }
        }

        if (result.status === 'success' && result.data && Array.isArray(result.data)) {
          // Process all vital signs records into days
          const processed = {};

          if (visitDate) {
            const startDate = new Date(visitDate);
            startDate.setHours(0, 0, 0, 0);

            result.data.forEach(record => {
              // Backend now returns `date` (Y-m-d). Prefer it over created_at for day calculation.
              const recordDateStr = record.date || record.vital_sign_date || record.created_at;
              const recordDate = new Date(recordDateStr);
              recordDate.setHours(0, 0, 0, 0);

              const diffTime = recordDate - startDate;
              const dayOffset = Math.floor(diffTime / (1000 * 60 * 60 * 24));
              const dayNumber = dayOffset + 1;

              if (dayNumber > 0) {
                processed[dayNumber] = {
                  id: record.id,
                  serial: record.serial || '',
                  temperature: record.temperature ? record.temperature.replace('°C', '') : '',
                  heartRate: record.heart_rate ? record.heart_rate.replace(' bpm', '') : '',
                  respiratoryRate: record.respiratory_rate ? record.respiratory_rate.replace(' breaths/min', '') : '',
                  capillaryRefillTime: record.capillary_refill_time || '',
                  bodyWeight: record.body_weight || '',
                  hydrationStatus: record.hydration_status || '',
                  mucousMembraneColor: record.mucous_membrane_color || '',
                  bodyConditionScore: record.body_condition_score || '',
                  urine: record.urine || '',
                  stool: record.stool || '',
                  oralCavity: record.oral_cavity || record.oralCavity || '',
                  lymphNodes: record.lymph_nodes || '',
                  severity: record.severity || '',
                  objective: record.objective || record.physical_exam_findings || '',
                  date: record.date || recordDateStr // keep date for reference/debug
                };
              }
            });

            setAllVitalSigns(processed);

            // Load data for the currently selected day (or the one we just defaulted to)
            // We need to know which day is effectively selected now.
            // Since state updates are async, we use the logic:
            // const effectivelySelectedDay = (days.length > 0 && (!selectedDay || selectedDay > days.length)) 
            //       ? days[days.length - 1] 
            //       : (selectedDay || 1);

            // Wait, 'days' state from above setDays won't be available immediately here?
            // YES, good catch. 'days' above refers to the State which isn't updated yet.
            // We should use the calculated 'daysArray'.
            // We also need to recalculate totalDays here to be safe if we rely on it.
            // Re-use logic:
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const startDateForCalc = new Date(visitDate);
            startDateForCalc.setHours(0, 0, 0, 0);
            const diffTimeCalc = today - startDateForCalc;
            const diffDaysCalc = Math.max(0, Math.floor(diffTimeCalc / (1000 * 60 * 60 * 24)));
            const totalDaysCalc = diffDaysCalc + 1;

            const currentDayToLoad = totalDaysCalc; // Default to latest day on load

            // If we are just loading, we probably want to show the latest data?
            // Or preserve selectedDay if we are re-fetching?
            // Let's assume we load the LAST available day (TODAY) initially.
            if (!hasFetchedVitalSignsRef.current) { // Only on initial fetch
              setSelectedDay(currentDayToLoad);
              const dayData = processed[currentDayToLoad] || {};
              setVitalSigns(prev => ({ ...prev, ...dayData }));
              if (dayData.severity) setSoapData(prev => ({ ...prev, severity: dayData.severity }));
              if (dayData.objective) setSoapData(prev => ({ ...prev, objective: dayData.objective }));
            } else {
              // If re-fetching (e.g. after save), refresh the currently selected day
              const dayData = processed[selectedDay] || {};
              setVitalSigns(prev => ({ ...prev, ...dayData }));
              // Also sync severity and objective to soapData
              if (dayData.severity) setSoapData(prev => ({ ...prev, severity: dayData.severity }));
              if (dayData.objective) setSoapData(prev => ({ ...prev, objective: dayData.objective }));
            }
          }
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        // Don't show toast for vital signs loading errors as they're not critical
      }
    } catch (error) {
      // Don't show toast for vital signs loading errors as they're not critical
    }
  };

  const handleInputChange = (field, value) => {
    setSoapData(prev => ({
      ...prev,
      [field]: value
    }));

    // If updating fields that are also in vital signs (severity, objective),
    // update allVitalSigns map too so it persists when switching days
    if (field === 'severity' || field === 'objective') {
      setAllVitalSigns(prev => ({
        ...prev,
        [selectedDay]: {
          ...(prev[selectedDay] || {}),
          [field]: value
        }
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleVitalSignsChange = (field, value) => {
    setVitalSigns(prev => {
      const newData = { ...prev, [field]: value };
      // Optimistically update the allVitalSigns map too
      setAllVitalSigns(all => ({
        ...all,
        [selectedDay]: {
          ...(all[selectedDay] || {}),
          [field]: value
        }
      }));
      return newData;
    });
  };

  // Handle Day Switch
  const handleDaySelect = (day) => {
    setSelectedDay(day);
    const dayData = allVitalSigns[day] || {};

    // Update Vital Signs Form
    setVitalSigns(() => ({
      serial: dayData.serial || '',
      temperature: dayData.temperature || '',
      heartRate: dayData.heartRate || '',
      respiratoryRate: dayData.respiratoryRate || '',
      capillaryRefillTime: dayData.capillaryRefillTime || '',
      bodyWeight: dayData.bodyWeight || '',
      hydrationStatus: dayData.hydrationStatus || '',
      mucousMembraneColor: dayData.mucousMembraneColor || '',
      bodyConditionScore: dayData.bodyConditionScore || '',
      urine: dayData.urine || '',
      stool: dayData.stool || '',
      oralCavity: dayData.oralCavity || '',
      lymphNodes: dayData.lymphNodes || ''
    }));

    // Update Linked Fields (Severity, Objective)
    setSoapData(prev => ({
      ...prev,
      severity: dayData.severity || '',
      objective: dayData.objective || ''
    }));
  };

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    const missingFields = [];

    if (!soapData.ownerComplaint.trim()) {
      newErrors.ownerComplaint = t('receptionOutpatients.details.soapNotes.errors.ownerComplaint', { defaultValue: "Guardian's complaint is required" });
      missingFields.push('Guardian Complaint');
    }

    if (!soapData.severity) {
      newErrors.severity = t('receptionOutpatients.details.soapNotes.errors.painScore', { defaultValue: 'Pain score is required' });
      missingFields.push('Pain score');
    }

    if (!soapData.objective.trim()) {
      newErrors.objective = t('receptionOutpatients.details.soapNotes.errors.objective', { defaultValue: 'Objective findings are required' });
      missingFields.push('Physical Exam Findings');
    }

    if (!soapData.initialDiagnosis.trim()) {
      newErrors.initialDiagnosis = t('receptionOutpatients.details.soapNotes.errors.initialDiagnosis', { defaultValue: 'Initial diagnosis is required' });
      missingFields.push('Initial Diagnosis');
    }

    setErrors(newErrors);

    // Show toast message if there are validation errors
    if (Object.keys(newErrors).length > 0) {
      const errorMessage = missingFields.length === 1
        ? `Please fill in the required field: ${missingFields[0]}`
        : `Please fill in the required fields: ${missingFields.join(', ')}`;
      showErrorToast(errorMessage, {
        title: 'Validation Error',
        duration: 5000
      });
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');

      // Prepare payload with appropriate visit ID
      const payload = {
        serial: soapData.serial,
        date_time: soapData.dateTime,
        owner_complaint: soapData.ownerComplaint,
        objective: soapData.objective,
        severity: soapData.severity,
        initial_diagnosis: soapData.initialDiagnosis,
        assessment: soapData.assessment,
        plan: soapData.plan,
        diagnosis_history: soapData.history,
        final_diagnosis: (soapData.initialDiagnosis && soapData.initialDiagnosis.trim())
          ? soapData.initialDiagnosis.trim()
          : (soapData.history && soapData.history.length > 0 ? soapData.history[soapData.history.length - 1].diagnosis : soapData.initialDiagnosis)
      };

      // Add the appropriate visit ID based on visit type
      if (visitType === 'inpatient') {
        payload.inpatient_visit_id = parseInt(inpatientId);
      } else {
        payload.outpatient_visit_id = parseInt(outpatientId);
      }


      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/soap-notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        // Also save vital signs if any fields are filled
        const hasVitalSigns = Object.values(vitalSigns).some(val => val && String(val).trim() !== '');
        if (hasVitalSigns) {
          await saveVitalSigns();
        }

        // Update visit's initial diagnosis if provided
        if (soapData.initialDiagnosis && onInitialDiagnosisUpdate) {
          try {
            await onInitialDiagnosisUpdate(soapData.initialDiagnosis);
          } catch (error) {
            // Don't show error toast as SOAP was saved successfully
          }
        }

        // Call parent's onSave callback (which will show toast and refresh)
        onSave && onSave();
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message ||
          (errorData.errors ? Object.values(errorData.errors).flat().join(', ') : null) ||
          'Failed to save SOAP notes';
        showErrorToast(errorMessage, {
          title: 'Save Error',
          duration: 5000
        });
      }
    } catch (error) {
      const errorMessage = error.message || 'An error occurred while saving SOAP notes. Please try again.';
      showErrorToast(errorMessage, {
        title: 'Error',
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const saveVitalSigns = async () => {
    try {
      const promises = [];
      const token = localStorage.getItem('access_token');

      // Iterate through all days in allVitalSigns and save them
      // This ensures edits on Day 1 are saved even if we are currently looking at Day 2
      const daysToSave = Object.keys(allVitalSigns).length > 0
        ? Object.keys(allVitalSigns)
        : [selectedDay]; // Fallback if map is empty (shouldn't happen if edits occurred)

      for (const dayStr of daysToSave) {
        const day = parseInt(dayStr);
        const dayData = allVitalSigns[day] || vitalSigns; // Fallback to current state if map missing logic

        // Skip empty records if they don't have an ID (don't create empty spam)
        const hasContent = dayData.temperature || dayData.heartRate || dayData.respiratoryRate ||
          dayData.objective || dayData.severity || dayData.notes;
        if (!dayData.id && !hasContent) continue;

        // Prepare payload from the day's data (NOT just current state)
        const payload = {
          serial: dayData.serial || '',
          temperature: dayData.temperature ? `${parseFloat(dayData.temperature)}°C` : '',
          heart_rate: dayData.heartRate ? `${parseInt(dayData.heartRate)} bpm` : '',
          respiratory_rate: dayData.respiratoryRate ? `${parseInt(dayData.respiratoryRate)} breaths/min` : '',
          capillary_refill_time: dayData.capillaryRefillTime || '',
          body_weight: parseFloat(dayData.bodyWeight) || 0,
          mucous_membrane_color: dayData.mucousMembraneColor || '',
          body_condition_score: dayData.bodyConditionScore || '',
          hydration_status: dayData.hydrationStatus || '',
          urine: dayData.urine || '',
          stool: dayData.stool || '',
          oral_cavity: dayData.oralCavity || '',
          lymph_nodes: dayData.lymphNodes || '',
          severity: dayData.severity || '', // Use data from map
          objective: dayData.objective || '' // Use data from map
        };

        if (visitType === 'inpatient') {
          payload.inpatient_visit_id = parseInt(inpatientId);
        } else {
          payload.outpatient_visit_id = parseInt(outpatientId);
        }

        // Calculate Date for this specific day
        if (visitInfo && visitInfo.visitDate) {
          const targetDate = new Date(visitInfo.visitDate);
          targetDate.setDate(targetDate.getDate() + (day - 1));
          const dateStr = targetDate.toISOString().slice(0, 19).replace('T', ' ');
          payload.date = dateStr;
        }

        let url = `${import.meta.env.VITE_API_URL}/api/v1/vital-signs`;
        let method = 'POST';

        if (dayData.id) {
          url = `${url}/${dayData.id}`;
          method = 'PUT';
          payload.id = dayData.id;
        }

        promises.push(
          fetch(url, {
            method: method,
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(payload)
          }).then(async res => {
            if (res.ok) {
              const json = await res.json();
              // Update ID in state if created
              if (method === 'POST' && json.data?.id) {
                setAllVitalSigns(prev => ({
                  ...prev,
                  [day]: {
                    ...prev[day],
                    id: json.data.id
                  }
                }));
              }
              return { success: true, day };
            } else {
              const err = await res.json().catch(() => ({}));
              return { success: false, day, error: err };
            }
          })
        );
      }

      const results = await Promise.all(promises);
      const failures = results.filter(r => !r.success);

      if (failures.length > 0) {
        showErrorToast('Some vital signs data failed to save.', {
          title: 'Partial Save Error',
          duration: 5000
        });
      }

    } catch (error) {
      showErrorToast(error.message || 'An error occurred while saving vital signs. Please try again.', {
        title: 'Error',
        duration: 5000
      });
    }
  };

  const handleCancel = () => {
    // Reset form to original state
    setSoapData({
      serial: '',
      dateTime: new Date().toISOString().slice(0, 16),
      ownerComplaint: '',
      subjective: '',
      severity: '',
      objective: '',
      initialDiagnosis: '',
      assessment: '',
      plan: ''
    });
    setVitalSigns({
      serial: '',
      temperature: '',
      heartRate: '',
      respiratoryRate: '',
      capillaryRefillTime: '',
      bodyWeight: '',
      hydrationStatus: '',
      mucousMembraneColor: '',
      bodyConditionScore: '',
      lymphNodes: ''
    });
    setErrors({});
    setOpenSections({
      subjective: false,
      objective: false,
      assessment: false,
      plan: false
    });
    onClose();
  };

  if (!isVisible) return null;

  // Calculate Display Final Diagnosis for read-only view
  let displayFinalDiagnosis = finalDiagnosis || soapData.finalDiagnosis;
  const currentInput = soapData.initialDiagnosis ? soapData.initialDiagnosis.trim() : '';

  if (!displayFinalDiagnosis) {
    if (currentInput) {
      displayFinalDiagnosis = currentInput;
    } else if (soapData.history && soapData.history.length > 0) {
      displayFinalDiagnosis = soapData.history[soapData.history.length - 1].diagnosis; // Latest history is at the end
    } else {
      displayFinalDiagnosis = soapData.initialDiagnosis;
    }
  }

  // Format services for display in Plan section
  /*
    const formattedServices = (services || [])
      .map(s => {
        const name = s.service_name_en || s.name || 'Service';
        const qty = s.quantity || s.qty || 1;
        const isFulfilled = s.is_fulfilled || s.fulfilled;
        const statusIcon = isFulfilled ? '✅' : '⏳';
        return `${statusIcon} ${name} (x${qty})`;
      }).join('\n');
  */

  return (
    <div className={`soap-details-content-view ${hideHeader ? 'soap-embedded' : ''}`} dir={isRTL ? 'rtl' : 'ltr'} style={{ textAlign: isRTL ? 'right' : 'left' }}>
      {/* Modern Header */}
      {!loadingExisting && !hideHeader && (
        <div className="soap-modern-header">
          <div>
            <h2 className="soap-modern-title">{t('receptionOutpatients.details.soapNotes.title', { defaultValue: 'SOAP Notes' })}</h2>
            {visitInfo && (
              <div className="soap-modern-info">
                {visitInfo.serial && (
                  <span className="soap-modern-info-badge">#{visitInfo.serial}</span>
                )}
                {visitInfo.petName && (
                  <span className="soap-modern-info-badge">{t('receptionOutpatients.details.soapNotes.pet', { defaultValue: 'Pet' })}: {visitInfo.petName}</span>
                )}
                {visitInfo.ownerName && (
                  <span className="soap-modern-info-badge">{t('receptionOutpatients.details.soapNotes.guardian', { defaultValue: 'Guardian' })}: {visitInfo.ownerName}</span>
                )}
              </div>
            )}
          </div>
          <div>
            <button
              onClick={handleCancel}
              disabled={loading}
              className="action-btn cancel-btn"
            >
              {t('common.close', { defaultValue: 'Close' })}
            </button>
          </div>
        </div>
      )}

      {/* Modern Save Bar */}
      {!readOnly && (
        <div className="soap-modern-save-bar">
          <button
            onClick={handleSave}
            disabled={loading || loadingExisting}
            className="action-btn save-btn soap-save-btn-large"
          >
            {loading ? (
              <>
                <div className="loading-spinner-small"></div>
                {t('common.saving', { defaultValue: 'Saving...' })}
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Save SOAP
              </>
            )}
          </button>
        </div>
      )}

      {/* Scrollable Content */}
      <div className="overlay-scrollable-content">
        {/* Loading State */}
        {loadingExisting && (
          <div className="overlay-loading">
            <div className="loading-spinner"></div>
            <p>{t('receptionOutpatients.details.soapNotes.loading', { defaultValue: 'Loading SOAP notes...' })}</p>
          </div>
        )}

        {/* Content */}
        {!loadingExisting && (
          <div className="soap-accordion-container">
            {/* S - Subjective */}
            <div className="soap-modern-accordion-item">
              <button
                type="button"
                className="soap-modern-accordion-header"
                onClick={() => toggleSection('subjective')}
              >
                <div className="soap-modern-section-icon subjective">S</div>
                <div className="soap-modern-section-title">
                  <h3>{t('receptionOutpatients.details.soapNotes.sections.subjective.title', { defaultValue: 'Subjective' })}</h3>
                  <p>{t('receptionOutpatients.details.soapNotes.sections.subjective.description', { defaultValue: "Guardian's complaint & pet's history" })}</p>
                </div>
                <svg
                  className={`soap-modern-accordion-arrow ${openSections.subjective ? 'open' : ''}`}
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <div className={`soap-modern-accordion-content ${openSections.subjective ? 'open' : ''}`}>
                <div className="soap-field">
                  <label className="soap-label">
                    {t('receptionOutpatients.details.soapNotes.sections.subjective.label', { defaultValue: "Guardian's Complaint & Pet's History" })} {!readOnly && <span className="required">*</span>}
                  </label>
                  {readOnly ? (
                    <div className="soap-modern-readonly">
                      {soapData.ownerComplaint || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>{t('receptionOutpatients.details.soapNotes.sections.subjective.notRecorded', { defaultValue: 'Not recorded' })}</span>}
                    </div>
                  ) : (
                    <>
                      <textarea
                        value={soapData.ownerComplaint}
                        onChange={(e) => handleInputChange('ownerComplaint', e.target.value)}
                        placeholder={t('receptionOutpatients.details.soapNotes.sections.subjective.placeholder', { defaultValue: "Describe the guardian's complaint and the pet's medical history..." })}
                        rows={5}
                        className={`soap-modern-textarea ${errors.ownerComplaint ? 'error' : ''}`}
                      />
                      {errors.ownerComplaint && (
                        <p className="error-message">{errors.ownerComplaint}</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* O - Objective */}
            <div className="soap-modern-accordion-item">
              <button
                type="button"
                className="soap-modern-accordion-header"
                onClick={() => toggleSection('objective')}
              >
                <div className="soap-modern-section-icon objective">O</div>
                <div className="soap-modern-section-title">
                  <h3>{t('receptionOutpatients.details.soapNotes.sections.objective.title', { defaultValue: 'Objective' })}</h3>
                  <p>{t('receptionOutpatients.details.soapNotes.sections.objective.description', { defaultValue: 'Vital signs & physical examination findings' })}</p>
                </div>
                <svg
                  className={`soap-modern-accordion-arrow ${openSections.objective ? 'open' : ''}`}
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {/* Vital Signs Grid */}
              <div className={`soap-modern-accordion-content ${openSections.objective ? 'open' : ''}`}>
                {/* Vital Signs Section */}
                <div className="soap-modern-vitals-container">
                  <h4 className="soap-modern-vitals-title">Vital Signs</h4>

                  {/* Day Tabs */}
                  {visitType === 'inpatient' && days.length > 0 && (
                    <div className="days-tabs-container" style={{ marginBottom: '16px', display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
                      {days.map(day => (
                        <button
                          key={day}
                          onClick={() => handleDaySelect(day)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid',
                            borderColor: selectedDay === day ? '#10B981' : '#E5E7EB',
                            backgroundColor: selectedDay === day ? '#ECFDF5' : '#FFFFFF',
                            color: selectedDay === day ? '#047857' : '#6B7280',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            transition: 'all 0.2s',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Day {day}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="soap-modern-vitals-grid">
                    {readOnly ? (
                      /* READ-ONLY VIEW */
                      <>
                        {/* Vital Sign Read-Only Cards */}
                        {[
                          { label: 'Temperature', value: vitalSigns.temperature, unit: '°C' },
                          { label: 'Heart Rate', value: vitalSigns.heartRate, unit: 'BPM' },
                          { label: 'Respiratory Rate', value: vitalSigns.respiratoryRate, unit: 'BPM' },
                          { label: 'CRT', value: vitalSigns.capillaryRefillTime, unit: '' },
                          { label: 'Weight', value: vitalSigns.bodyWeight, unit: 'kg' },
                          { label: 'Hydration', value: vitalSigns.hydrationStatus, unit: '' },
                          { label: 'Mucous Membrane', value: vitalSigns.mucousMembraneColor, unit: '' },
                          { label: 'BCS', value: vitalSigns.bodyConditionScore, unit: '' },
                          { label: 'Urine', value: vitalSigns.urine, unit: '' },
                          { label: 'Stool', value: vitalSigns.stool, unit: '' },
                          { label: 'Oral Cavity', value: vitalSigns.oralCavity, unit: '' },
                          { label: 'Lymph Nodes', value: vitalSigns.lymphNodes, unit: '' },
                        ].map((sign, index) => (
                          <div key={index} className="vital-sign-card-readonly" style={{
                            background: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '10px',
                            padding: '12px 16px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px'
                          }}>
                            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              {sign.label}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                              <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>
                                {sign.value || '-'}
                              </span>
                              {sign.unit && sign.value && (
                                <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#64748b' }}>
                                  {sign.unit}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}

                        {/* Pain Score Read-Only */}
                        <div className="soap-field" style={{ gridColumn: 'span 4', marginTop: '8px' }}>
                          <div style={{
                            background: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '10px',
                            padding: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px'
                          }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              Pain Score
                            </span>
                            {['0', '1', '2', '3', '4'].map((level) => {
                              const isActive = String(vitalSigns.severity || soapData.severity) === level;
                              if (!isActive) return null; // Only show the selected one or none if empty

                              const getLevelColors = (lvl) => {
                                switch (lvl) {
                                  case '0': return { border: '#10B981', bg: '#ECFDF5', text: '#047857', label: '0 - No Pain' };
                                  case '1': return { border: '#FCD34D', bg: '#FFFBEB', text: '#B45309', label: '1 - Mild' };
                                  case '2': return { border: '#F97316', bg: '#FFF7ED', text: '#9A3412', label: '2 - Moderate' };
                                  case '3': return { border: '#EF4444', bg: '#FEF2F2', text: '#B91C1C', label: '3 - Severe' };
                                  case '4': return { border: '#991B1B', bg: '#FEF2F2', text: '#7F1D1D', label: '4 - Extreme' };
                                  default: return { border: '#E5E7EB', bg: '#FFFFFF', text: '#6B7280', label: lvl };
                                }
                              };
                              const colors = getLevelColors(level);
                              return (
                                <div key={level} style={{
                                  padding: '6px 16px',
                                  borderRadius: '20px',
                                  border: `1px solid ${colors.border}`,
                                  backgroundColor: colors.bg,
                                  color: colors.text,
                                  fontWeight: 600,
                                  fontSize: '0.9rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}>
                                  <span style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '50%',
                                    background: colors.text,
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.8rem'
                                  }}>
                                    {level}
                                  </span>
                                  {colors.label.split(' - ')[1]}
                                </div>
                              );
                            })}
                            {!(vitalSigns.severity || soapData.severity) && (
                              <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Not recorded</span>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      /* EDITABLE VIEW (Existing logic) */
                      <>
                        {/* Temperature */}
                        <div className="vital-sign-card-premium">
                          <label className="soap-label" style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: '#374151', textTransform: 'uppercase' }}>Temperature</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="number"
                              step="0.1"
                              value={vitalSigns.temperature}
                              onChange={(e) => !readOnly && handleVitalSignsChange('temperature', e.target.value)}
                              readOnly={readOnly}
                              disabled={readOnly}
                              placeholder="-"
                              className="soap-input"
                              style={{ MozAppearance: 'textfield', paddingRight: '35px', textAlign: 'center', fontWeight: 600, ...(readOnly ? { backgroundColor: '#FFFFFF', cursor: 'not-allowed', opacity: 0.8 } : {}) }}
                            />
                            <span className="vital-sign-unit-badge">°C</span>
                          </div>
                        </div>


                        {/* Heart Rate */}
                        <div className="vital-sign-card-premium">
                          <label className="soap-label" style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: '#374151', textTransform: 'uppercase' }}>Heart Rate</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="number"
                              step="1"
                              min="0"
                              value={vitalSigns.heartRate}
                              onChange={(e) => {
                                if (!readOnly) {
                                  const sanitized = e.target.value.replace(/[^0-9]/g, '');
                                  handleVitalSignsChange('heartRate', sanitized);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (readOnly || ['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              readOnly={readOnly}
                              disabled={readOnly}
                              placeholder="-"
                              className="soap-input"
                              style={{ MozAppearance: 'textfield', paddingRight: '40px', textAlign: 'center', fontWeight: 600, ...(readOnly ? { backgroundColor: '#FFFFFF', cursor: 'not-allowed', opacity: 0.8 } : {}) }}
                            />
                            <span className="vital-sign-unit-badge">BPM</span>
                          </div>
                        </div>

                        {/* Respiratory Rate */}
                        <div className="vital-sign-card-premium">
                          <label className="soap-label" style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: '#374151', textTransform: 'uppercase' }}>Respiratory Rate</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="number"
                              value={vitalSigns.respiratoryRate}
                              onChange={(e) => !readOnly && handleVitalSignsChange('respiratoryRate', e.target.value)}
                              readOnly={readOnly}
                              disabled={readOnly}
                              placeholder="-"
                              className="soap-input"
                              style={{ MozAppearance: 'textfield', paddingRight: '40px', textAlign: 'center', fontWeight: 600, ...(readOnly ? { backgroundColor: '#FFFFFF', cursor: 'not-allowed', opacity: 0.8 } : {}) }}
                            />
                            <span className="vital-sign-unit-badge">BPM</span>
                          </div>
                        </div>

                        {/* Capillary Refill Time */}
                        <div className="vital-sign-card-premium">
                          <label className="soap-label" style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: '#374151', textTransform: 'uppercase' }}>CRT</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              value={vitalSigns.capillaryRefillTime}
                              onChange={(e) => !readOnly && handleVitalSignsChange('capillaryRefillTime', e.target.value)}
                              readOnly={readOnly}
                              disabled={readOnly}
                              placeholder="-"
                              className="soap-input"
                              style={{ textAlign: 'center', fontWeight: 600, ...(readOnly ? { backgroundColor: '#FFFFFF', cursor: 'not-allowed', opacity: 0.8 } : {}) }}
                            />
                          </div>
                        </div>

                        {/* Body Weight */}
                        <div className="vital-sign-card-premium">
                          <label className="soap-label" style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: '#374151', textTransform: 'uppercase' }}>Weight</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="number"
                              step="0.1"
                              value={vitalSigns.bodyWeight}
                              onChange={(e) => !readOnly && handleVitalSignsChange('bodyWeight', e.target.value)}
                              readOnly={readOnly}
                              disabled={readOnly}
                              placeholder="-"
                              className="soap-input"
                              style={{ MozAppearance: 'textfield', paddingRight: '35px', textAlign: 'center', fontWeight: 600, ...(readOnly ? { backgroundColor: '#FFFFFF', cursor: 'not-allowed', opacity: 0.8 } : {}) }}
                            />
                            <span className="vital-sign-unit-badge">kg</span>
                          </div>
                        </div>

                        {/* Hydration Status */}
                        <div className="vital-sign-card-premium">
                          <label className="soap-label" style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: '#374151', textTransform: 'uppercase' }}>Hydration</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              value={vitalSigns.hydrationStatus}
                              onChange={(e) => !readOnly && handleVitalSignsChange('hydrationStatus', e.target.value)}
                              readOnly={readOnly}
                              disabled={readOnly}
                              placeholder="-"
                              className="soap-input"
                              style={{ textAlign: 'center', fontWeight: 600, ...(readOnly ? { backgroundColor: '#FFFFFF', cursor: 'not-allowed', opacity: 0.8 } : {}) }}
                            />
                          </div>
                        </div>

                        {/* Mucous Membrane Color */}
                        <div className="vital-sign-card-premium">
                          <label className="soap-label" style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: '#374151', textTransform: 'uppercase' }}>Mucous Membrane</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              value={vitalSigns.mucousMembraneColor}
                              onChange={(e) => !readOnly && handleVitalSignsChange('mucousMembraneColor', e.target.value)}
                              readOnly={readOnly}
                              disabled={readOnly}
                              placeholder="-"
                              className="soap-input"
                              style={{ textAlign: 'center', fontWeight: 600, ...(readOnly ? { backgroundColor: '#FFFFFF', cursor: 'not-allowed', opacity: 0.8 } : {}) }}
                            />
                          </div>
                        </div>

                        {/* Body Condition Score */}
                        <div className="vital-sign-card-premium">
                          <label className="soap-label" style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: '#374151', textTransform: 'uppercase' }}>BCS</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              value={vitalSigns.bodyConditionScore}
                              onChange={(e) => !readOnly && handleVitalSignsChange('bodyConditionScore', e.target.value)}
                              readOnly={readOnly}
                              disabled={readOnly}
                              placeholder="-"
                              className="soap-input"
                              style={{ textAlign: 'center', fontWeight: 600, ...(readOnly ? { backgroundColor: '#FFFFFF', cursor: 'not-allowed', opacity: 0.8 } : {}) }}
                            />
                          </div>
                        </div>

                        {/* Urine */}
                        <div className="vital-sign-card-premium">
                          <label className="soap-label" style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: '#374151', textTransform: 'uppercase' }}>Urine</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              value={vitalSigns.urine}
                              onChange={(e) => !readOnly && handleVitalSignsChange('urine', e.target.value)}
                              readOnly={readOnly}
                              disabled={readOnly}
                              placeholder="-"
                              className="soap-input"
                              style={{ textAlign: 'center', fontWeight: 600, ...(readOnly ? { backgroundColor: '#FFFFFF', cursor: 'not-allowed', opacity: 0.8 } : {}) }}
                            />
                          </div>
                        </div>

                        {/* Stool */}
                        <div className="vital-sign-card-premium">
                          <label className="soap-label" style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: '#374151', textTransform: 'uppercase' }}>Stool</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              value={vitalSigns.stool}
                              onChange={(e) => !readOnly && handleVitalSignsChange('stool', e.target.value)}
                              readOnly={readOnly}
                              disabled={readOnly}
                              placeholder="-"
                              className="soap-input"
                              style={{ textAlign: 'center', fontWeight: 600, ...(readOnly ? { backgroundColor: '#FFFFFF', cursor: 'not-allowed', opacity: 0.8 } : {}) }}
                            />
                          </div>
                        </div>

                        {/* Oral Cavity */}
                        <div className="vital-sign-card-premium">
                          <label className="soap-label" style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: '#374151', textTransform: 'uppercase' }}>Oral Cavity</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              value={vitalSigns.oralCavity}
                              onChange={(e) => !readOnly && handleVitalSignsChange('oralCavity', e.target.value)}
                              readOnly={readOnly}
                              disabled={readOnly}
                              placeholder="-"
                              className="soap-input"
                              style={{ textAlign: 'center', fontWeight: 600, ...(readOnly ? { backgroundColor: '#FFFFFF', cursor: 'not-allowed', opacity: 0.8 } : {}) }}
                            />
                          </div>
                        </div>

                        {/* Lymph Nodes */}
                        <div className="vital-sign-card-premium">
                          <label className="soap-label" style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: '#374151', textTransform: 'uppercase' }}>Lymph Nodes</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              value={vitalSigns.lymphNodes}
                              onChange={(e) => !readOnly && handleVitalSignsChange('lymphNodes', e.target.value)}
                              readOnly={readOnly}
                              disabled={readOnly}
                              placeholder="-"
                              className="soap-input"
                              style={{ textAlign: 'center', fontWeight: 600, ...(readOnly ? { backgroundColor: '#FFFFFF', cursor: 'not-allowed', opacity: 0.8 } : {}) }}
                            />
                          </div>
                        </div>

                        {/* Severity / Pain Score */}
                        <div className="soap-field" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: 0, gridColumn: 'span 4' }}>
                          <label className="soap-label" style={{ margin: 0, fontSize: '13px', minWidth: '170px' }}>
                            Pain Score {!readOnly && <span className="required" style={{ color: '#EF4444' }}>*</span>}
                          </label>
                          <div style={{ display: 'flex', gap: '12px', flex: 1 }}>
                            {['0', '1', '2', '3', '4'].map((level) => {
                              const getLevelColors = (lvl) => {
                                switch (lvl) {
                                  case '0': return { border: '#10B981', bg: '#ECFDF5', text: '#047857', label: '0' };
                                  case '1': return { border: '#FCD34D', bg: '#FFFBEB', text: '#B45309', label: '1' };
                                  case '2': return { border: '#F97316', bg: '#FFF7ED', text: '#9A3412', label: '2' };
                                  case '3': return { border: '#EF4444', bg: '#FEF2F2', text: '#B91C1C', label: '3' };
                                  case '4': return { border: '#991B1B', bg: '#FEF2F2', text: '#7F1D1D', label: '4' };
                                  default: return { border: '#E5E7EB', bg: '#FFFFFF', text: '#6B7280', label: lvl };
                                }
                              };
                              const colors = getLevelColors(level);
                              const isActive = String(soapData.severity) === level;

                              return (
                                <div
                                  key={level}
                                  onClick={() => !readOnly && handleInputChange('severity', level)}
                                  style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: '1px solid',
                                    borderColor: isActive ? colors.border : '#E5E7EB',
                                    backgroundColor: isActive ? colors.bg : '#FFFFFF',
                                    cursor: readOnly ? 'default' : 'pointer',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: isActive ? colors.text : '#6B7280',
                                    textAlign: 'center',
                                    minWidth: '60px',
                                    transition: 'all 0.2s',
                                    userSelect: 'none'
                                  }}
                                >
                                  {colors.label}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {errors.severity && !readOnly && (
                    <p className="error-message" style={{ marginTop: '8px', fontSize: '12px', color: '#EF4444', paddingLeft: '92px' }}>{errors.severity}</p>
                  )}
                </div>



                {/* Physical Exam Findings */}
                <div className="soap-field" style={{ marginTop: '24px' }}>
                  <label className="soap-label">
                    {t('receptionOutpatients.details.soapNotes.sections.objective.physicalExam', { defaultValue: 'Physical Examination Findings' })} {!readOnly && <span className="required">*</span>}
                  </label>
                  {readOnly ? (
                    <div className="soap-modern-readonly">
                      {soapData.objective || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>{t('receptionOutpatients.details.soapNotes.sections.subjective.notRecorded', { defaultValue: 'Not recorded' })}</span>}
                    </div>
                  ) : (
                    <>
                      <textarea
                        value={soapData.objective}
                        onChange={(e) => handleInputChange('objective', e.target.value)}
                        placeholder={t('receptionOutpatients.details.soapNotes.sections.objective.placeholder', { defaultValue: 'Enter physical examination findings...' })}
                        rows={6}
                        className={`soap-modern-textarea ${errors.objective ? 'error' : ''}`}
                      />
                      {errors.objective && (
                        <p className="error-message">{errors.objective}</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* A - Assessment */}
            <div className="soap-modern-accordion-item">
              <button
                type="button"
                className="soap-modern-accordion-header"
                onClick={() => toggleSection('assessment')}
              >
                <div className="soap-modern-section-icon assessment">A</div>
                <div className="soap-modern-section-title">
                  <h3>{t('receptionOutpatients.details.soapNotes.sections.assessment.title', { defaultValue: 'Assessment' })}</h3>
                  <p>{t('receptionOutpatients.details.soapNotes.sections.assessment.description', { defaultValue: 'Diagnosis & clinical assessment' })}</p>
                </div>
                <svg
                  className={`soap-modern-accordion-arrow ${openSections.assessment ? 'open' : ''}`}
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <div className={`soap-modern-accordion-content ${openSections.assessment ? 'open' : ''}`}>

                <div className="soap-field">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label className="soap-label" style={{ marginBottom: 0 }}>
                      {soapData.history && soapData.history.length > 0 
                        ? t('receptionOutpatients.details.soapNotes.sections.assessment.differentialDiagnosis', { defaultValue: 'Differential Diagnosis' })
                        : t('receptionOutpatients.details.soapNotes.sections.assessment.initialDiagnosis', { defaultValue: 'Initial Diagnosis' })} {!readOnly && <span className="required">*</span>}
                    </label>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={handleAddDifferential}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 12px',
                          backgroundColor: '#EFF6FF',
                          color: '#2563EB',
                          border: '1px solid #BFDBFE',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        title="Add as distinct differential diagnosis"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="5" x2="12" y2="19"></line>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        Add Differential
                      </button>
                    )}
                  </div>
                  {readOnly ? (
                    // Read Only View - Show text only if exists, otherwise rely on history list
                    soapData.initialDiagnosis ? (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        color: '#374151',
                        fontSize: '14px'
                      }}>
                        {soapData.initialDiagnosis}
                      </div>
                    ) : (
                      // If no text and no history, show 'Not recorded'. If history exists, the list below handles it.
                      (!soapData.history || soapData.history.length === 0) && (
                        <div style={{ color: '#9ca3af', fontStyle: 'italic', padding: '8px 0' }}>Not recorded</div>
                      )
                    )
                  ) : (
                    <>
                      <textarea
                        value={soapData.initialDiagnosis}
                        onChange={(e) => handleInputChange('initialDiagnosis', e.target.value)}
                        placeholder={t('receptionOutpatients.details.soapNotes.sections.assessment.placeholder', { defaultValue: 'Enter your clinical assessment and diagnosis...' })}
                        rows={5}
                        className={`soap-modern-textarea ${errors.initialDiagnosis ? 'error' : ''}`}
                      />
                      {errors.initialDiagnosis && (
                        <p className="error-message">{errors.initialDiagnosis}</p>
                      )}
                    </>
                  )}
                </div>

                {/* Differential Diagnosis History */}
                {soapData.history && soapData.history.length > 0 && (
                  <div className="soap-field" style={{ marginTop: '16px' }}>

                    <div className="soap-diagnosis-history" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {[...soapData.history].reverse().map((entry, index) => {
                        const originalIndex = soapData.history.length - 1 - index;
                        return (
                          <div key={index} style={{ position: 'relative' }}>
                            <label className="soap-label" style={{
                              fontSize: '12px',
                              color: '#6B7280',
                              marginBottom: '4px',
                              display: 'flex',
                              justifyContent: 'space-between'
                            }}>
                              <span>{originalIndex === 0 
                                ? t('receptionOutpatients.details.soapNotes.sections.assessment.initialDiagnosis', { defaultValue: 'Initial Diagnosis' })
                                : `${t('receptionOutpatients.details.soapNotes.sections.assessment.differentialDiagnosis', { defaultValue: 'Differential Diagnosis' })} ${originalIndex}`}</span>
                              <span>{entry.by} • {new Date(entry.date).toLocaleString()}</span>
                            </label>
                            <textarea
                              value={entry.diagnosis}
                              disabled
                              rows={4}
                              className="soap-textarea"
                              style={{
                                backgroundColor: '#F9FAFB',
                                cursor: 'not-allowed',
                                opacity: 0.8,
                                color: '#374151'
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="soap-field">
                  <label className="soap-label">
                    {t('receptionOutpatients.details.soapNotes.sections.assessment.finalDiagnosis', { defaultValue: 'Final Diagnosis' })}
                  </label>
                  <textarea
                    value={displayFinalDiagnosis}
                    readOnly
                    placeholder={finalDiagnosis ? '' : t('receptionOutpatients.details.soapNotes.sections.assessment.autoCalculated', { defaultValue: 'Automatically calculated from the last diagnosis' })}
                    rows={4}
                    className="soap-textarea"
                    style={{
                      backgroundColor: finalDiagnosis ? '#FFFFFF' : '#F9FAFB',
                      cursor: 'not-allowed',
                      opacity: finalDiagnosis ? 1 : 0.7
                    }}
                  />
                  {!finalDiagnosis && !readOnly && (
                    <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                      {t('receptionOutpatients.details.soapNotes.sections.assessment.availableAfterCheckout', { defaultValue: 'Final diagnosis will be available after checkout' })}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Checkout Summary */}
            {(effectiveVisitInfo?.doctorNotes || effectiveVisitInfo?.prescription || effectiveVisitInfo?.finalDiagnosis) && (
              <div className="soap-accordion-item">
                <div className="soap-accordion-header" style={{ cursor: 'default' }}>
                  <span className="soap-letter">{t('receptionOutpatients.details.soapNotes.sections.summary.title', { defaultValue: 'Summary' })}</span>
                  <span className="soap-text">{t('receptionOutpatients.details.soapNotes.sections.summary.description', { defaultValue: 'Checkout Summary' })}</span>
                </div>
                <div className="soap-accordion-content open">
                  {effectiveVisitInfo?.doctorNotes ? (
                    <div className="soap-field">
                      <label className="soap-label">{t('receptionOutpatients.details.soapNotes.sections.summary.doctorNotes', { defaultValue: 'Doctor Notes' })}</label>
                      <textarea
                        value={effectiveVisitInfo.doctorNotes}
                        readOnly
                        className="soap-textarea"
                        style={{ backgroundColor: '#F9FAFB', cursor: 'not-allowed' }}
                      />
                    </div>
                  ) : null}
                  {effectiveVisitInfo?.prescription ? (
                    <div className="soap-field">
                      <label className="soap-label">{t('receptionOutpatients.details.soapNotes.sections.summary.prescription', { defaultValue: 'Prescription' })}</label>
                      <textarea
                        value={effectiveVisitInfo.prescription}
                        readOnly
                        className="soap-textarea"
                        style={{ backgroundColor: '#F9FAFB', cursor: 'not-allowed' }}
                      />
                    </div>
                  ) : null}
                  {effectiveVisitInfo?.finalDiagnosis ? (
                    <div className="soap-field">
                      <label className="soap-label">{t('receptionOutpatients.details.soapNotes.sections.assessment.finalDiagnosisCheckout', { defaultValue: 'Final Diagnosis (Checkout)' })}</label>
                      <textarea
                        value={effectiveVisitInfo.finalDiagnosis}
                        readOnly
                        className="soap-textarea"
                        style={{ backgroundColor: '#F9FAFB', cursor: 'not-allowed' }}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {/* P - Plan */}
            <div className="soap-modern-accordion-item">
              <button
                type="button"
                className="soap-modern-accordion-header"
                onClick={() => toggleSection('plan')}
              >
                <div className="soap-modern-section-icon plan">P</div>
                <div className="soap-modern-section-title">
                  <h3>{t('receptionOutpatients.details.soapNotes.sections.plan.title', { defaultValue: 'Plan' })}</h3>
                  <p>{t('receptionOutpatients.details.soapNotes.sections.plan.description', { defaultValue: 'Treatment plan & services' })}</p>
                </div>
                <svg
                  className={`soap-modern-accordion-arrow ${openSections.plan ? 'open' : ''}`}
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <div className={`soap-modern-accordion-content ${openSections.plan ? 'open' : ''}`}>
                {/* Services Section - Primary content of Plan */}
                {showPlanServices && planServices && planServices.length > 0 ? (
                  <div className="soap-field">
                    <label className="soap-label" style={{ marginBottom: '16px', fontSize: '15px', fontWeight: 700 }}>{t('receptionOutpatients.details.soapNotes.sections.plan.servicesMedications', { defaultValue: 'Services & Medications' })}</label>
                    <div className="soap-modern-services-grid">
                      {planServices.map((service, index) => {
                        const isFulfilled = service.is_fulfilled || service.fulfilled;
                        return (
                          <div key={service.id || index} className={`soap-modern-service-card ${isFulfilled ? 'fulfilled' : ''}`}>
                            <div className="soap-modern-service-check">
                              <i className={`fas ${isFulfilled ? 'fa-check' : 'fa-circle'}`}></i>
                            </div>
                            <div className="soap-modern-service-content">
                              <div className="soap-modern-service-name">
                                {service.name || service.service_name_en || `Service #${service.id || index + 1}`}
                              </div>
                              <div className="soap-modern-service-meta">
                                {(service.quantity || service.qty) && (
                                  <span className="soap-modern-service-qty">{t('receptionOutpatients.details.soapNotes.sections.plan.quantity', { defaultValue: 'Qty:' })} {service.quantity || service.qty}</span>
                                )}
                              </div>
                              {service.notes && (
                                <div className="soap-modern-service-notes">{service.notes}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {soapData.plan && soapData.plan.trim() && (
                      <div className="soap-field" style={{ marginTop: '20px' }}>
                        <div style={{
                          padding: '16px',
                          backgroundColor: '#fefce8',
                          borderRadius: '12px',
                          border: '1px solid #fef08a',
                          color: '#854d0e',
                          fontSize: '14px',
                          fontFamily: '"Red Hat Display", sans-serif',
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.6'
                        }}>
                          <strong>{t('receptionOutpatients.details.soapNotes.sections.plan.treatmentPlanNotes', { defaultValue: 'Treatment Plan Notes:' })}</strong><br />
                          {soapData.plan}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="soap-field">
                    {soapData.plan && soapData.plan.trim() ? (
                      <div style={{
                        padding: '16px',
                        backgroundColor: '#fefce8',
                        borderRadius: '12px',
                        border: '1px solid #fef08a',
                        color: '#854d0e',
                        fontSize: '14px',
                        fontFamily: '"Red Hat Display", sans-serif',
                        whiteSpace: 'pre-wrap',
                        lineHeight: '1.6'
                      }}>
                        <strong>Treatment Plan Notes:</strong><br />
                        {soapData.plan}
                      </div>
                    ) : (
                      <p style={{ color: '#94a3b8', fontSize: '14px', fontStyle: 'italic', margin: 0 }}>
                        {t('receptionOutpatients.details.soapNotes.sections.plan.noServices', { defaultValue: 'No services added to this visit.' })}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
        }
      </div >
    </div >
  );
};

export default SOAPDetailsContent;

